import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X, Globe, ShieldCheck } from 'lucide-react';
import { MetaLabel, PlayfairHeading, NobelDivider } from '.';

interface CustomsGateProps {
    isOpen: boolean;
    onClose: () => void;
    feature?: string;
    reason?: 'daily_limit' | 'premium_feature' | 'elite_feature';
}

export const CustomsGate: React.FC<CustomsGateProps> = ({
    isOpen,
    onClose,
    feature = 'RESTRICTED AREA',
    reason = 'premium_feature'
}) => {

    const handleAcquireClearance = () => {
        window.dispatchEvent(new CustomEvent('ielts-navigate-pricing'));
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a0ae0] backdrop-blur-md p-6"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#F9F8F4] border-2 border-[var(--color-nobel-gold)] p-0 max-w-lg w-full relative shadow-2xl overflow-hidden"
                    >
                        {/* Header Bar */}
                        <div className="bg-[var(--color-stone-900)] text-[var(--color-nobel-gold)] p-3 flex justify-between items-center">
                            <MetaLabel style={{ color: 'var(--color-nobel-gold)' }}>IMMIGRATION CONTROL</MetaLabel>
                            <button onClick={onClose} className="hover:text-white transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-10 text-center relative">
                            {/* Watermark */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
                                <Globe size={300} />
                            </div>

                            <div className="mb-6 flex justify-center">
                                <div className="p-4 border border-[var(--color-nobel-gold)] rounded-full">
                                    <Lock size={32} className="text-[var(--color-nobel-gold)]" />
                                </div>
                            </div>

                            <MetaLabel style={{ color: 'var(--color-cancelled)', marginBottom: '1rem' }}>
                                CUSTOMS FEE REQUIRED
                            </MetaLabel>

                            <PlayfairHeading size="2xl" style={{ lineHeight: '1.2' }}>
                                Clearance Required for<br />
                                <span className="italic text-[var(--color-nobel-gold)]">{feature}</span>
                            </PlayfairHeading>

                            <NobelDivider style={{ margin: '2rem auto' }} />

                            <p className="body-inter text-[var(--color-stone-600)] mb-8 max-w-sm mx-auto">
                                Access to this terminal requires <span className="font-bold text-[var(--color-stone-900)]">Permanent Resident</span> status or higher. An immigration processing fee applies.
                            </p>

                            <div className="bg-[var(--color-stone-white)] border border-[var(--color-stone-200)] p-4 mb-6 text-center">
                                <MetaLabel style={{ fontSize: '0.625rem', marginBottom: '0.5rem' }}>ACCEPTED PAYMENT METHODS</MetaLabel>
                                <div className="space-y-1">
                                    <p className="body-inter text-xs text-[var(--color-stone-600)]">CCP (Compte Courant Postal)</p>
                                    <p className="body-inter text-xs text-[var(--color-stone-600)]">BaridiMob</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <button
                                    onClick={handleAcquireClearance}
                                    className="w-full py-4 bg-[var(--color-nobel-gold)] text-[var(--color-stone-900)] font-bold tracking-widest text-xs uppercase hover:bg-[#B48F48] transition-colors shadow-lg"
                                >
                                    Acquire Fast-Track Clearance
                                </button>

                                <button
                                    onClick={onClose}
                                    className="w-full py-3 text-[var(--color-stone-500)] text-xs font-bold tracking-widest uppercase hover:text-[var(--color-stone-900)] transition-colors"
                                >
                                    Return to Transit Lounge
                                </button>
                            </div>

                            <div className="mt-8 flex justify-center items-center gap-2 text-[10px] text-[var(--color-stone-400)] font-mono uppercase">
                                <ShieldCheck size={12} />
                                <span>Official Checkpoint • ID: {Math.random().toString(36).substr(2, 6).toUpperCase()}</span>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
